import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import ProcessStrip from "../components/ProcessStrip";
import ConversionCTA from "../components/ConversionCTA";
import CalculatorCrossLink from "../components/CalculatorCrossLink";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { CheckCircle2, ShieldCheck, FileSignature, Sparkles, MapPin, FileText, ChevronRight } from "lucide-react";
import { PRIORITY_STATES, STATE_SLUGS } from "../lib/constants";
import { getStateGuides } from "../data/state_guides";
import LazyYouTube from "../components/LazyYouTube";

const HERO_IMG = "https://images.unsplash.com/photo-1761014586544-53fe5e1f1e25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjYXIlMjBrZXlzJTIwaGFuZHNoYWtlfGVufDB8fHx8MTc3ODQ5NjIyMnww&ixlib=rb-4.1.0&q=85";

const STATE_IMAGES = {
  California: "https://images.unsplash.com/photo-1663328319236-86eee3352311?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxjYWxpZm9ybmlhJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3ODQ5NjIyMnww&ixlib=rb-4.1.0&q=85",
  Texas: "https://images.unsplash.com/photo-1592190057402-2bf1ee02118d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHx0ZXhhcyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3Nzg0OTYyMzN8MA&ixlib=rb-4.1.0&q=85",
  Florida: "https://images.unsplash.com/photo-1560427791-bcf0b027ff44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwYmVhY2h8ZW58MHx8fHwxNzc4NDk2MjMzfDA&ixlib=rb-4.1.0&q=85",
  "New York": "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmV8ZW58MHx8fHwxNzc4NDk2MjMzfDA&ixlib=rb-4.1.0&q=85",
};

const BENEFITS = [
  "Free to start",
  "State-specific guidance",
  "As-is sale clause",
  "Odometer disclosure fields",
  "Digital signatures",
  "PDF copy for buyer and seller",
  "Photo attachments",
  "No account required to start",
];

const FAQS = [
  { q: "What is a car bill of sale?", a: "A car bill of sale is a written record of a private vehicle sale between a seller and a buyer. It documents the transaction details, vehicle info, price, and terms." },
  { q: "Do I need a bill of sale to sell a car privately?", a: "Many states recommend or require one. Even where not required, it protects both parties by documenting the sale, the odometer reading, and as-is terms." },
  { q: "Is a bill of sale the same as a title transfer?", a: "No. A bill of sale documents the sale. A title transfer is the official DMV process to legally change ownership records. You usually need both." },
  { q: "Can I sell a car as-is?", a: "Yes. An as-is sale means the seller offers no warranty. The buyer accepts the vehicle in its current condition. Our template includes a clear as-is clause." },
  { q: "Should I include the odometer reading?", a: "Yes — most states require an odometer disclosure on vehicle transfers, with limited exceptions for older or specialty vehicles." },
  { q: "Do both buyer and seller need to sign?", a: "Yes. Both parties should sign and date the bill of sale. CarSaleDoc captures both signatures digitally and emails both parties a copy." },
  { q: "Can I attach photos to the bill of sale?", a: "Yes. You can attach exterior, interior, odometer and VIN photos which appear as labeled exhibits in the final PDF." },
  { q: "Does CarSaleDoc replace DMV forms?", a: "No. State DMVs may require additional forms (title transfer, release of liability, etc.). CarSaleDoc generates a bill of sale to accompany those forms." },
  { q: "Is this legal advice?", a: "No. CarSaleDoc is not a law firm. The document is a general template based on your inputs. Consult a qualified professional for legal advice." },
  { q: "Can I use this for motorcycles or trailers?", a: "Yes. You can select Car, Truck, Motorcycle, Trailer, RV, Boat or Other when filling out the wizard." },
];

export default function Landing({ stateName = null, variant = null }) {
  const isStatePage = !!stateName;
  const stateSlug = stateName ? STATE_SLUGS[stateName] : null;

  const h1 = isStatePage ? `${stateName} Car Bill of Sale` : "Create and sign a car bill of sale in minutes.";
  const sub = isStatePage
    ? `Create a ${stateName} car bill of sale online. Add buyer, seller, vehicle, odometer, payment and as-is terms. Sign digitally and send a completed PDF to both parties.`
    : "Add buyer, seller, vehicle, odometer, payment and as-is terms. Sign online and send a completed PDF copy to both parties.";

  // SEO config per route
  const seoTitle = isStatePage
    ? `${stateName} Car Bill of Sale | Create & Sign Online`
    : variant === "main"
      ? "Car Bill of Sale | Create & Sign Online"
      : "CarSaleDoc — Create and sign a car bill of sale in minutes";
  const seoDescription = isStatePage
    ? `Create a ${stateName} car bill of sale online. Add vehicle, buyer, seller, odometer, payment and as-is terms. Sign digitally and send a completed PDF.`
    : "Create a car bill of sale online. Add buyer, seller, vehicle, odometer, payment and as-is terms. Sign digitally and send a completed PDF to both parties.";
  // Canonical URL — both "/" and "/car-bill-of-sale" render the same
  // Landing component; we set both to canonical="/" so Google collapses
  // duplicate structured data (including the VideoObject) onto a single
  // watch page. Prevents "video not on a watch page" GSC warning.
  const seoPath = isStatePage ? `/${stateSlug}` : "/";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": isStatePage ? `${stateName} Vehicle Bill of Sale` : "Vehicle Bill of Sale",
    "provider": { "@type": "Organization", "name": "CarSaleDoc" },
    "areaServed": isStatePage ? { "@type": "State", "name": stateName } : { "@type": "Country", "name": "United States" },
    "offers": { "@type": "Offer", "price": "4.99", "priceCurrency": "USD" },
  };

  const jsonLd = [faqJsonLd, productJsonLd];

  return (
    <Layout>
      <SEO title={seoTitle} description={seoDescription} path={seoPath} jsonLd={jsonLd} />
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 csd-grid-bg opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            {isStatePage && (
              <div className="inline-flex items-center gap-2 mb-5 text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)] bg-[var(--csd-primary-light)] px-3 py-1 rounded-full" data-testid="hero-state-badge">
                <MapPin size={14} /> {stateName}
              </div>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 leading-[1.05]" data-testid="hero-h1">
              {h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--csd-text-secondary)] leading-relaxed max-w-xl" data-testid="hero-sub">
              {sub}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/builder/new" data-testid="hero-cta-primary">
                <Button className="csd-btn-primary px-6 h-12 text-base font-semibold rounded-xl">
                  Create My Bill of Sale
                </Button>
              </Link>
              <Link to="/sample-pdf" data-testid="hero-cta-secondary">
                <Button variant="outline" className="h-12 px-6 rounded-xl text-base">View Sample PDF</Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              <span className="csd-trust-badge"><ShieldCheck size={14} /> Free to start</span>
              <span className="csd-trust-badge"><Sparkles size={14} /> No account required</span>
              <span className="csd-trust-badge"><FileSignature size={14} /> Digital signatures</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-[0_20px_80px_rgba(13,90,56,0.18)] aspect-[4/5] max-h-[560px] mx-auto">
              <img src={HERO_IMG} alt="Car keys handshake" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 bg-white border border-[var(--csd-border)] rounded-2xl p-4 shadow-lg max-w-xs">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--csd-primary-light)] grid place-items-center text-[var(--csd-primary)]">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <div className="font-semibold text-sm">Completed & signed</div>
                  <div className="text-xs text-[var(--csd-text-secondary)]">PDF emailed to both parties.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits strip */}
      <section className="max-w-7xl mx-auto px-6 py-8 border-y border-[var(--csd-border)] bg-white">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="text-[var(--csd-primary)] mt-0.5 shrink-0" size={16} />
              <span className="text-[var(--csd-text-primary)]">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works — 3-minute flow strip */}
      <ProcessStrip />

      {/* Explainer video (main landing only — state pages have richer market
          data instead). LazyYouTube ships only a poster image until the user
          clicks Play, so it adds ~10 KB to LCP, not the YouTube SDK. */}
      {!isStatePage && (
        <section className="max-w-5xl mx-auto px-6 py-12">
          <div className="max-w-2xl mx-auto text-center mb-6">
            <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">See it in action</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">
              How CarSaleDoc works — in under 3 minutes
            </h2>
            <p className="mt-3 text-base text-[var(--csd-text-secondary)]">
              Watch how a private seller and buyer go from blank form to a fully signed bill of sale, without any back-and-forth.
            </p>
          </div>
          <LazyYouTube
            id="zqdSmMoNjw4"
            title="How CarSaleDoc works — Create, sign and send a Car Bill of Sale online"
            description="Step-by-step walkthrough of the CarSaleDoc wizard: enter vehicle details, scan ID, sign with the buyer, and download the signed bill of sale PDF."
            uploadDate="2026-06-29T00:00:00+00:00"
            durationISO="PT2M30S"
            testId="landing-explainer-video"
          />
        </section>
      )}

      {/* State cards */}
      {!isStatePage && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="max-w-2xl mb-8">
            <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">By state</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">State-specific checklists for the most active US markets.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRIORITY_STATES.map((s) => (
              <Link
                key={s}
                to={`/${STATE_SLUGS[s]}`}
                data-testid={`state-card-${s.toLowerCase().replace(" ", "-")}`}
                className="relative h-64 rounded-2xl overflow-hidden group block"
              >
                <img src={STATE_IMAGES[s]} alt={s} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/30 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-80">Bill of Sale</div>
                  <div className="font-heading text-2xl font-bold mt-1">{s}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* State Private Car Sale Guides — only on a state landing page that has guides */}
      {isStatePage && (() => {
        const stateKey = stateName.toLowerCase().replace(/\s+/g, "-");
        const guideState = getStateGuides(stateKey);
        if (!guideState) return null;
        return (
          <section
            className="max-w-7xl mx-auto px-6 py-16 border-t border-[var(--csd-border)]"
            data-testid="state-guides-section"
          >
            <div className="max-w-2xl mb-8">
              <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">
                {guideState.stateName} guides
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">
                {guideState.stateName} private car sale guides
              </h2>
              <p className="mt-4 text-[var(--csd-text-secondary)] leading-relaxed">
                Practical, plain-English guides to help {guideState.stateName} buyers and sellers complete a private car sale — bill of sale fields, odometer disclosure, title transfer, sales tax and what to do with plates and insurance.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {guideState.guides.map((g) => (
                <Link
                  key={g.slug}
                  to={`${guideState.basePath}/${g.slug}`}
                  data-testid={`state-guide-card-${g.slug}`}
                  className="csd-card p-6 group hover:border-[var(--csd-primary)] transition flex flex-col"
                >
                  <FileText className="text-[var(--csd-primary)] mb-3" size={20} />
                  <div className="font-heading font-bold text-lg leading-snug">{g.title}</div>
                  <div className="text-sm text-[var(--csd-text-secondary)] mt-2 leading-relaxed flex-1">
                    {g.cardDescription}
                  </div>
                  <div className="text-sm text-[var(--csd-primary)] font-semibold inline-flex items-center gap-1 mt-4">
                    Read guide <ChevronRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Why bill of sale */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">Why use a bill of sale</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">Documented sales protect everyone.</h2>
            <p className="mt-4 text-[var(--csd-text-secondary)] leading-relaxed">
              A bill of sale records the price, terms, vehicle details and parties involved in a private vehicle sale.
              It captures the odometer reading at the time of transfer, documents the as-is clause, and creates a paper
              trail for both buyer and seller.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["As-is sale explained", "Odometer information", "Signed by both parties", "PDF for buyer & seller"].map((t, i) => (
              <div key={i} className="csd-card p-5">
                <CheckCircle2 className="text-[var(--csd-primary)]" size={20} />
                <div className="font-semibold mt-3">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">FAQ</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">Common questions about car bills of sale.</h2>
        <div className="mt-8">
          <Accordion type="single" collapsible className="csd-card divide-y divide-gray-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="px-6 border-0">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="font-semibold text-base hover:text-[var(--csd-primary)]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[var(--csd-text-secondary)] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA — high-conversion block with testimonial + trust badges */}
      <ConversionCTA stateName={stateName} testId={isStatePage ? "final-cta-state" : "final-cta"} />

      {/* Cross-link to the free car value calculator */}
      <CalculatorCrossLink state={isStatePage ? stateName : null} testId="landing-calc-crosslink" />

      <p className="text-xs text-[var(--csd-text-secondary)] mt-2 mb-12 text-center max-w-3xl mx-auto px-6 leading-relaxed">
        CarSaleDoc is not a law firm and does not provide legal advice. This tool helps prepare a general bill of sale.
        State DMV requirements may vary. Always check your local DMV rules before completing a vehicle transfer.
      </p>
    </Layout>
  );
}
